import { PageHeader, Tabs, type TabItem } from "@gf/ui";
import { Bot, CreditCard, MessageSquareQuote, ShieldCheck, UserRound } from "lucide-react";
import { useSearchParams } from "react-router";
import { IgGlyph } from "../../components/IgGlyph";
import { LockedFeature } from "../../components/plans/LockedFeature";
import { PlanLock } from "../../components/plans/PlanCta";
import { useDocumentTitle } from "../../lib/hooks";
import { usePlan } from "../../lib/plan";
import { AiTab } from "./AiTab";
import { AutomationTab } from "./AutomationTab";
import { BillingTab } from "./BillingTab";
import { InstagramTab } from "./InstagramTab";
import { ProfileTab } from "./ProfileTab";
import { VoiceTab } from "./VoiceTab";
import { IntegrationsTab } from './IntegrationsTab';

type SettingsTab = "profile" | "billing" | "instagram" | "automation" | "voice" | "ai" | "integrations";

const TABS: TabItem<SettingsTab>[] = [
  { value: "profile", label: "Profile & password", icon: <UserRound /> },
  { value: "billing", label: "Billing", icon: <CreditCard /> },
  { value: "instagram", label: "Instagram accounts", icon: <IgGlyph /> },
  { value: "automation", label: "Automation & safety", icon: <ShieldCheck /> },
  { value: "voice", label: "Brand voice", icon: <MessageSquareQuote /> },
  { value: "ai", label: "AI models", icon: <Bot /> },
  { value: "integrations", label: "API & MCP", icon: <Bot /> },
];

function isTab(v: string | null): v is SettingsTab {
  return !!v && TABS.some((t) => t.value === v);
}

/** /settings?tab=profile|billing|instagram|automation|voice|ai|integrations */
export function SettingsPage() {
  const [params, setParams] = useSearchParams();
  const raw = params.get("tab");
  const tab: SettingsTab = isTab(raw) ? raw : "profile";
  useDocumentTitle("Settings");
  // API & MCP is a plan feature: a locked account sees the upgrade card, and the tab never calls the API.
  const integrations = usePlan().has("integrations");
  const items = integrations ? TABS : TABS.map((t) => (t.value === "integrations" ? { ...t, badge: <PlanLock /> } : t));

  return (
    <div className="gfp-page">
      <PageHeader title="Settings" description="Your profile, billing, your Instagram accounts, your AI, and how GramFather behaves on each account." />
      <Tabs
        label="Settings sections"
        items={items}
        value={tab}
        onChange={(v) => setParams((p) => {
          const next = new URLSearchParams(p);
          next.set("tab", v);
          return next;
        })}
      >
        {tab === "profile" ? (
          <ProfileTab />
        ) : tab === "billing" ? (
          <BillingTab />
        ) : tab === "instagram" ? (
          <InstagramTab />
        ) : tab === "automation" ? (
          <AutomationTab />
        ) : tab === "integrations" ? (
          integrations ? <IntegrationsTab /> : <LockedFeature feature="integrations" embedded />
        ) : tab === "ai" ? (
          <AiTab />
        ) : (
          <VoiceTab />
        )}
      </Tabs>
    </div>
  );
}
